import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
 ArrowLeft, Sun, Moon, Skull, Vote, Crosshair, ChevronRight, ChevronLeft,
 Eye, EyeOff, Trophy, X, Clock, FileText, Heart, RotateCcw, Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import OvalTable from '@/components/mafia/OvalTable';
import SpeechTimer from '@/components/mafia/SpeechTimer';
import {
 getGame, listRoles, nextRole, switchPhase, previousStep, eliminatePlayer, startGame,
 revivePlayer, addEvent, patchEvent, upsertRoundComment, endGame, updateGame,
} from '@/api/mafia';
import { useTranslation } from '@/i18n/LanguageContext';

const RED = '#DC2626';

const PHASE_ICON = {
 setup: Clock,
 first_night: Moon,
 night: Moon,
 day: Sun,
 game_over: Trophy,
};

// Per-role night action config. Keyed by canonical Russian role name.
// - picks: how many seats the host needs to select
// - actionKey: i18n key for the apply button label
// - eventAction: backend event "action" string (logged in events array)
// - kill: if true, eliminate each picked seat (uses /eliminate endpoint)
// - firstNightOnly / thirdNightOnly: contextual notes for the host
// - pickFromDead: if true, host should pick a dead seat (Хамелеон)
const ROLE_ACTIONS = {
 'Комиссар': { picks: 1, actionKey: 'role-action.check', eventAction: 'check' },
 'Доктор': { picks: 1, actionKey: 'role-action.heal', eventAction: 'heal' },
 'Маньяк': { picks: 1, actionKey: 'role-action.kill', eventAction: 'maniac_kill', kill: true },
 'Свидетель': { picks: { min: 1, max: 5 }, actionKey: 'role-action.watch', eventAction: 'watch' },
 'Шахид': { picks: { min: 1, max: 5 }, actionKey: 'role-action.bomb', eventAction: 'plant_bomb', firstNightOnly: true },
 'Политик': { picks: { min: 1, max: 3 }, actionKey: 'role-action.nominate', eventAction: 'nominate' },
 'Хамелеон': { picks: 1, actionKey: 'role-action.copy', eventAction: 'copy_role', thirdNightOnly: true, pickFromDead: true },
 'Исповедник': { picks: 1, actionKey: 'role-action.immunity', eventAction: 'immunity' },
 'Некромант': { picks: { min: 1, max: 2 }, actionKey: 'role-action.eternal-life', eventAction: 'eternal_life' },
 'Мафия': { picks: 1, actionKey: 'role-action.maf-kill', eventAction: 'mafia_shot', kill: true },
 'Дон': { picks: 1, actionKey: 'role-action.don-kill', eventAction: 'don_kill', kill: true },
 'Адвокат': { picks: 1, actionKey: 'role-action.excuse', eventAction: 'excuse' },
 'Путана': { picks: 1, actionKey: 'role-action.block', eventAction: 'block' },
 'Дилер': { picks: 1, actionKey: 'role-action.plant', eventAction: 'plant_mafia' },
 'Вор': { picks: 1, actionKey: 'role-action.steal', eventAction: 'steal' },
 'Оборотень': { picks: 1, actionKey: 'role-action.bite', eventAction: 'bite' },
 'Лжец': { picks: 1, actionKey: 'role-action.lie', eventAction: 'lie' },
};

// Resolve effective picks config: { min, max } where max=null means unlimited.
function resolvePicks(cfg) {
 if (!cfg) return { min: 0, max: 0, mode: 'exact' };
 const p = cfg.picks;
 if (p == null) return { min: 1, max: null, mode: 'range' };
 if (typeof p === 'number') return { min: p, max: p, mode: 'exact' };
 if (typeof p === 'object') return { min: p.min ?? 1, max: p.max ?? null, mode: 'range' };
 return { min: 0, max: 0, mode: 'exact' };
}

export default function MafiaGamePage() {
 const { id } = useParams();
 const navigate = useNavigate();
 const { t, tRole, tRoleDesc, tWinner } = useTranslation();
 const [game, setGame] = useState(null);
 const [rolesById, setRolesById] = useState({});
 const [loading, setLoading] = useState(true);
 const [showRoles, setShowRoles] = useState(true);
 const [selectedSeat, setSelectedSeat] = useState(null);
 const [voteTargets, setVoteTargets] = useState([]);
 const [nightTargets, setNightTargets] = useState([]);
 const [showEndModal, setShowEndModal] = useState(false);
 const [reportModal, setReportModal] = useState(null); // { round, phase } or null
 // Балаган nomination dropdowns (proposer + nominee for the day vote)
 const [nominator, setNominator] = useState(null);
 const [nominee, setNominee] = useState(null);
 // Day-phase sub-stage:
 //   'balagan'        → open discussion + nomination dropdowns
 //   'balagan_vote'   → 50%+ yes/no vote for each player nominated during Балаган
 //   'speeches'       → per-seat speeches
 //   'voting'         → per-voter normal voting
 //   'voting_results' → tally + apply lynch
 const [dayStage, setDayStage] = useState('balagan');
 const [speakerIdx, setSpeakerIdx] = useState(0);
 // Index into voteTargets for the immediate Балаган-vote stage
 const [balaganVoteIdx, setBalaganVoteIdx] = useState(0);
 // Normal voting: per-voter records
 const [voteRecords, setVoteRecords] = useState([]); // [{ voter_seat, target_seat | null }]
 const [currentVoterIdx, setCurrentVoterIdx] = useState(0);

 const refresh = async () => {
 try {
 const g = await getGame(id);
 setGame(g);
 return g;
 } catch (e) {
 console.error(e);
 toast.error('Не удалось загрузить игру');
 return null;
 }
 };

 useEffect(() => {
 (async () => {
 setLoading(true);
 try {
 const [g, rolesData] = await Promise.all([getGame(id), listRoles()]);
 const map = Object.fromEntries((rolesData.roles || []).map((r) => [r.id, r]));
 setRolesById(map);
 setGame(g);
 } catch (e) {
 toast.error('Не удалось загрузить игру');
 } finally {
 setLoading(false);
 }
 })();
 }, [id]);

 // Reset day stage and speaker index every time the game enters a fresh day
 // (we key off current_phase + current_round so a new day always starts at Балаган).
 useEffect(() => {
 if (game?.current_phase === 'day') {
 setDayStage('balagan');
 setSpeakerIdx(0);
 setNominator(null);
 setNominee(null);
 setBalaganVoteIdx(0);
 setVoteRecords([]);
 setCurrentVoterIdx(0);
 // Clear stale vote targets from previous days
 setVoteTargets([]);
 }
 }, [game?.current_phase, game?.current_round]);

 const alivePlayers = useMemo(() => (game?.players || []).filter((p) => p.alive), [game]);
 const deadPlayers = useMemo(() => (game?.players || []).filter((p) => !p.alive), [game]);

 const teamCounts = useMemo(() => {
 const counts = { red: 0, black: 0, gray: 0 };
 alivePlayers.forEach((p) => {
 const r = rolesById[p.role_id];
 if (r) counts[r.team] = (counts[r.team] || 0) + 1;
 });
 return counts;
 }, [alivePlayers, rolesById]);

 const currentNightRole = useMemo(() => {
 if (!game) return null;
 if (game.current_phase !== 'night' && game.current_phase !== 'first_night') return null;
 const queue = game.night_queue || [];
 const idx = game.night_action_index || 0;
 const rid = queue[idx];
 return rid ? rolesById[rid] : null;
 }, [game, rolesById]);

 // True when host has finished applying every role on the night queue —
 // signals "ready to wake the table" and reveals the Night Report.
 const nightDone = useMemo(() => {
 if (!game) return false;
 if (game.current_phase !== 'night' && game.current_phase !== 'first_night') return false;
 const queue = game.night_queue || [];
 const idx = game.night_action_index || 0;
 return queue.length > 0 && idx >= queue.length;
 }, [game]);

 // Build a report for an arbitrary {round, phase} pair. Reused by both the
 // inline night-report panel (current round) and the per-round popup modal.
 const buildReport = useCallback((round, phaseFilter) => {
 if (!game) return { deaths: [], actions: [], savedSet: new Set() };
 const isPhaseMatch = (ph) => {
 if (phaseFilter === 'night') return ph === 'night' || ph === 'first_night';
 return ph === phaseFilter;
 };
 const events = (game.events || []).filter(
 (ev) => ev.round === round && isPhaseMatch(ev.phase)
 );
 const seatInfo = (seat) => {
 const p = (game.players || []).find((pp) => pp.seat === seat);
 if (!p) return { seat, name: 'Игрок', role: null, alive: true };
 const r = p.role_id ? rolesById[p.role_id] : null;
 return { seat, name: (p.name && p.name.trim()) || 'Игрок', role: r, alive: p.alive };
 };

 const KILL_KINDS = new Set(['don_kill', 'mafia_shot', 'maniac_kill']);
 const PROT_KINDS = new Set(['heal', 'immunity', 'bite', 'eternal_life', 'shield']);
 const killIntents = new Map();
 const protections = new Map();
 events.forEach((ev) => {
 if (ev.voided) return; // Host-voided events are skipped by the algorithm
 if (KILL_KINDS.has(ev.action)) {
 (ev.target_seats || []).forEach((s) => {
 const list = killIntents.get(s) || []; list.push(ev.action); killIntents.set(s, list);
 });
 } else if (PROT_KINDS.has(ev.action)) {
 (ev.target_seats || []).forEach((s) => {
 const list = protections.get(s) || []; list.push(ev.action); protections.set(s, list);
 });
 }
 });

 const killedSet = new Set();
 const savedSet = new Set();
 const savedByMap = new Map();
 killIntents.forEach((kinds, seat) => {
 const info = seatInfo(seat);
 const role = info.role;
 const prot = protections.get(seat) || [];
 const reasons = [];
 if (role?.has_eternal_life) reasons.push('Вечная жизнь (роль)');
 if (prot.includes('eternal_life')) reasons.push('Вечная жизнь (Некромант)');
 if (prot.includes('heal')) reasons.push('Лечение (Доктор)');
 if (prot.includes('bite')) reasons.push('Укус (Оборотень)');
 if (prot.includes('shield')) reasons.push('Щит');
 const onlyManiac = kinds.every((k) => k === 'maniac_kill');
 if (onlyManiac) {
 if (prot.includes('immunity')) reasons.push('Иммунитет (Исповедник)');
 if (role?.name === 'Оборотень') reasons.push('Оборотень иммунен к Маньяку');
 if (role?.name === 'Пьяница') reasons.push('Пьяница иммунен к Маньяку');
 }
 if (reasons.length === 0) {
 killedSet.add(seat);
 } else {
 savedSet.add(seat);
 savedByMap.set(seat, reasons.join(' + '));
 }
 });

 // Pull explicit night_kill/night_save events (post-resolution, for past rounds)
 const finalKilledSeats = new Set();
 const finalSavedSeats = new Map();
 events.forEach((ev) => {
 if (ev.action === 'night_kill') (ev.target_seats || []).forEach((s) => finalKilledSeats.add(s));
 if (ev.action === 'night_save') (ev.target_seats || []).forEach((s) => finalSavedSeats.set(s, ev.note));
 });
 // If we have finalised events, they take precedence
 if (finalKilledSeats.size > 0 || finalSavedSeats.size > 0) {
 killedSet.clear();
 finalKilledSeats.forEach((s) => killedSet.add(s));
 finalSavedSeats.forEach((note, s) => { savedSet.add(s); savedByMap.set(s, note || '—'); });
 }

 const deaths = Array.from(killedSet).map((seat) => seatInfo(seat));
 // For day-phase reports: also collect day_lynch events as deaths
 if (phaseFilter === 'day') {
 events.forEach((ev) => {
 if (ev.action === 'day_lynch') (ev.target_seats || []).forEach((s) => deaths.push(seatInfo(s)));
 });
 }

 // Build "saved / under protection" list — every seat that received a protection
 // event this round (heal/immunity/bite/eternal_life/shield) PLUS seats whose
 // role makes them intrinsically immune (Пьяница). Each entry annotates what
 // protected them and whether a kill was actually averted.
 const PROT_LABELS = {
 heal: { name: 'Лечение', source: 'Доктор' },
 immunity: { name: 'Иммунитет от Маньяка', source: 'Исповедник' },
 bite: { name: 'Укус (защита 1 раз)', source: 'Оборотень' },
 eternal_life: { name: 'Вечная жизнь', source: 'Некромант' },
 shield: { name: 'Щит', source: '—' },
 };
 const savedSeats = new Map(); // seat -> { protections: [...], wasTargeted, savedFromKill }
 protections.forEach((kinds, seat) => {
 const info = seatInfo(seat);
 const wasTargeted = killIntents.has(seat);
 savedSeats.set(seat, {
 ...info,
 protections: kinds.map((k) => PROT_LABELS[k] || { name: k, source: '—' }),
 wasTargeted,
 savedFromKill: wasTargeted && savedSet.has(seat),
 });
 });
 // Role-intrinsic eternal life (e.g. Пьяница) — surface them even if no
 // event explicitly protected them this round.
 (game.players || []).forEach((p) => {
 const role = p.role_id ? rolesById[p.role_id] : null;
 if (!role || !role.has_eternal_life || !p.alive) return;
 if (!savedSeats.has(p.seat)) {
 savedSeats.set(p.seat, {
 seat: p.seat,
 name: (p.name && p.name.trim()) || 'Игрок',
 role,
 alive: p.alive,
 protections: [{ name: 'Вечная жизнь (роль)', source: role.name }],
 wasTargeted: killIntents.has(p.seat),
 savedFromKill: killIntents.has(p.seat) && savedSet.has(p.seat),
 });
 } else {
 savedSeats.get(p.seat).protections.push({ name: 'Вечная жизнь (роль)', source: role.name });
 }
 });
 const saved = Array.from(savedSeats.values());

 const ACTION_LABELS = {
 check: { verb: 'Проверил', outcomeFor: (t, ev) => {
 if (typeof ev.correct === 'boolean') return ev.correct ? 'Чёрный' : 'Красный';
 if (t.role) return t.role.team === 'black' ? 'Чёрный' : t.role.team === 'red' ? 'Красный' : 'Серый';
 return '—';
 }},
 don_check: { verb: 'Проверил (Дон)', outcomeFor: (t, ev) =>
 typeof ev.correct === 'boolean' ? (ev.correct ? 'Комиссар' : 'Не Комиссар') : '—' },
 don_kill: { verb: 'Убил', outcomeFor: (t) =>
 savedSet.has(t.seat) ? `Спасён: ${savedByMap.get(t.seat)}` : (killedSet.has(t.seat) ? 'Убит' : '—') },
 mafia_shot: { verb: 'Выстрел мафии в', outcomeFor: (t) =>
 savedSet.has(t.seat) ? `Спасён: ${savedByMap.get(t.seat)}` : (killedSet.has(t.seat) ? 'Убит' : '—') },
 maniac_kill: { verb: 'Убил (маньяк)', outcomeFor: (t) =>
 savedSet.has(t.seat) ? `Спасён: ${savedByMap.get(t.seat)}` : (killedSet.has(t.seat) ? 'Убит' : '—') },
 heal: { verb: 'Лечит', outcomeFor: () => 'Под защитой от убийства' },
 immunity: { verb: 'Даёт иммунитет', outcomeFor: () => 'Иммунитет от Маньяка' },
 bite: { verb: 'Укусил', outcomeFor: () => 'Защищён один раз; при линче → Оборотень' },
 lie: { verb: 'Оболгал', outcomeFor: () => 'Результат проверки инвертирован' },
 steal: { verb: 'Украл способность у', outcomeFor: () => 'Способность украдена' },
 excuse: { verb: 'Дал отмазку', outcomeFor: () => 'Иммунитет от дневного линча' },
 block: { verb: 'Заблокировал', outcomeFor: () => 'Ночное действие отменено' },
 plant_mafia: { verb: 'Подсадил', outcomeFor: () => 'С 3-й ночи становится мафией' },
 plant_bomb: { verb: 'Заложил бомбу', outcomeFor: () => 'Заминирован' },
 watch: { verb: 'Следит за', outcomeFor: () => 'Под наблюдением' },
 nominate: { verb: 'Выставил на голосование', outcomeFor: () => 'На дневном голосовании' },
 copy_role: { verb: 'Скопировал роль', outcomeFor: (t) => t.role ? `Стал «${t.role.name}»` : 'Роль скопирована' },
 eternal_life: { verb: 'Дал вечную жизнь', outcomeFor: () => 'Бессмертен' },
 shield: { verb: 'Дал щит', outcomeFor: () => 'Защищён от ночного убийства' },
 bless: { verb: 'Благословил', outcomeFor: () => 'Узнал роль убитого' },
 day_lynch: { verb: 'Линчёван', outcomeFor: () => 'Изгнан днём' },
 balagan_vote: { verb: 'Балаган-голосование', outcomeFor: (t, ev) =>
 ev.correct === true ? 'Изгнан (>50%)' : ev.correct === false ? 'Оставлен (<50%)' : (ev.note || '—') },
 voting_cast: { verb: 'Голосует за', outcomeFor: (tg) =>
 tg.seat ? `Голос за ${tg.name}` : 'Воздержался' },
 voting_result: { verb: 'Итог голосования', outcomeFor: (tg, ev) =>
 ev.note ? ev.note.split('|')[0].trim() : `Победитель ${tg.name}` },
 };

 const actions = events
 .filter((ev) => !['night_kill', 'night_save', 'night_action'].includes(ev.action))
 .map((ev) => {
 const meta = ACTION_LABELS[ev.action] || { verb: ev.action, outcomeFor: () => '' };
 const actor = ev.actor_seat != null ? seatInfo(ev.actor_seat) : null;
 const actorRole = actor?.role || (ev.note ? { name: ev.note, icon: '' } : null);
 const targets = (ev.target_seats || []).map((seat) => {
 const ti = seatInfo(seat);
 const willDie = KILL_KINDS.has(ev.action) && killedSet.has(seat) && !ev.voided;
 const baseOutcome = meta.outcomeFor(ti, ev);
 // host_override (if set) replaces the computed outcome
 const outcome = ev.host_override || baseOutcome;
 return { ...ti, outcome, willDie };
 });
 return {
 id: ev.id,
 action: ev.action,
 verb: meta.verb,
 actor,
 actorRole,
 targets,
 voided: !!ev.voided,
 hostOverride: ev.host_override || null,
 hostComment: ev.host_comment || null,
 };
 });
 return { deaths, actions, saved, savedSet };
 }, [game, rolesById]);

 // Inline night report (current round only)
 const nightReport = useMemo(
 () => buildReport(game?.current_round || 1, 'night'),
 [buildReport, game?.current_round]
 );

 // Seats holding day-immunity for the current round (from this round's
 // night `excuse` events that aren't voided + this round's `day_immunity`
 // events granted during the day).
 const dayImmunitySeats = useMemo(() => {
 if (!game) return [];
 const round = game.current_round || 1;
 const seats = new Set();
 (game.events || []).forEach((ev) => {
 if (ev.round !== round || ev.voided) return;
 if (ev.action === 'excuse' || ev.action === 'day_immunity') {
 (ev.target_seats || []).forEach((s) => seats.add(s));
 }
 });
 return Array.from(seats);
 }, [game]);

 // Resolve a seat to its player name. "Name-first" UI rule: numbers are
 // never shown — when a player has no name, fall back to "Игрок" without
 // exposing the internal seat index in user-facing copy.
 const seatName = useCallback((seat) => {
 if (seat == null) return '—';
 const p = (game?.players || []).find((pp) => pp.seat === seat);
 if (p && p.name && p.name.trim()) return p.name.trim();
 return 'Игрок';
 }, [game?.players]);

 // Grant day-immunity from the table's hover button — logs a day_immunity
 // event that the night-resolution and day-vote flow can honor.
 const handleGrantImmunity = useCallback(async (seat) => {
 try {
 await addEvent(id, {
 action: 'day_immunity',
 target_seats: [seat],
 note: 'Иммунитет (хост)',
 });
 const g = await refresh();
 if (g) toast.success(`Иммунитет дан · ${seatName(seat)}`);
 } catch (e) {
 toast.error('Не удалось дать иммунитет');
 }
 }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

 if (loading || !game) {
 return (
 <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]">
 {t('common.loading')}
 </div>
 );
 }

 const phase = game.current_phase;
 const isSetup = phase === 'setup';
 const isFirstNight = phase === 'first_night';
 const isNight = phase === 'night' || phase === 'first_night';
 const isDay = phase === 'day';
 const isOver = phase === 'game_over';
 const PhaseIcon = PHASE_ICON[phase] || Clock;

 const toggleVote = (seat) => {
 setVoteTargets((prev) => prev.includes(seat) ? prev.filter((s) => s !== seat) : [...prev, seat]);
 };
 const toggleNight = (seat) => {
 const cfg = currentNightRole ? ROLE_ACTIONS[currentNightRole.name] : null;
 const { max } = resolvePicks(cfg);
 setNightTargets((prev) => {
 if (prev.includes(seat)) return prev.filter((s) => s !== seat);
 // max=null means unlimited; otherwise replace oldest when full
 if (max != null && prev.length >= max) {
 return [...prev.slice(1), seat];
 }
 return [...prev, seat];
 });
 };

 const onSeatClick = (seat) => {
 if (isOver) return;
 // Dead players cannot be targets of votes or night actions, and can't speak.
 const target = (game?.players || []).find((p) => p.seat === seat);
 const targetDead = target && !target.alive;
 if (!targetDead) {
 if (isDay) toggleVote(seat);
 else if (isNight) toggleNight(seat);
 }
 // Selecting a dead seat for inspection is still allowed (review-only).
 setSelectedSeat(seat);
 };

 const handleEliminate = async (seat, reason) => {
 try {
 const g = await eliminatePlayer(id, { seat, reason });
 setGame(g);
 setVoteTargets([]);
 setNightTargets([]);
 toast.success(`Seat ${seat} eliminated`);
 } catch (e) {
 toast.error('Failed to eliminate');
 }
 };

 const handleEliminateVotes = async () => {
 if (voteTargets.length === 0) {
 toast.error('Никого не выбрано');
 return;
 }
 for (const seat of voteTargets) {
 // eslint-disable-next-line no-await-in-loop
 await handleEliminate(seat, 'day_lynch');
 }
 };

 const handleNightKill = async () => {
 if (nightTargets.length === 0) {
 toast.error('Никого не выбрано');
 return;
 }
 for (const seat of nightTargets) {
 // eslint-disable-next-line no-await-in-loop
 await handleEliminate(seat, 'night_kill');
 }
 };

 const handleRevive = async (seat) => {
 try {
 const g = await revivePlayer(id, { seat, reason: 'other' });
 setGame(g);
 toast.success(`Seat ${seat} restored`);
 } catch (e) {
 toast.error('Failed to revive');
 }
 };

 const handleNextRole = async () => {
 try {
   if (nightTargets.length > 0) {
     await addEvent(id, {
       action: 'night_action',
       target_seats: nightTargets,
       note: currentNightRole ? `${currentNightRole.name} action` : 'Night action',
     });
     setNightTargets([]);
   }
   const g = await nextRole(id);
   setGame(g);
 } catch (e) {
   toast.error('Failed to advance role');
 }
 };

 const handlePhaseAction = async () => {
 try {
   // Setup → start (Meeting Night)
   if (isSetup) {
     const g = await startGame(id);
     setGame(g);
     return;
   }
   // If exiting night, log any pending night targets
   if (isNight && nightTargets.length > 0) {
     await addEvent(id, {
       action: 'night_action',
       target_seats: nightTargets,
       note: currentNightRole ? `${currentNightRole.name} action` : 'Night action',
     });
     setNightTargets([]);
   }
   const g = await switchPhase(id);
   setGame(g);
 } catch (e) {
   toast.error('Failed to switch phase');
 }
 };

 // Patch an event with host overrides and refresh the game state
 const patchEventLocal = async (eventId, payload) => {
 try {
 const g = await patchEvent(id, eventId, payload);
 setGame(g);
 return g;
 } catch (e) {
 toast.error('Не удалось обновить событие');
 return null;
 }
 };

 // Set / clear the host comment for an entire round
 const saveRoundComment = async (round, phase, comment) => {
 try {
 const g = await upsertRoundComment(id, { round, phase, comment });
 setGame(g);
 toast.success(comment.trim() ? 'Комментарий сохранён' : 'Комментарий удалён');
 } catch (e) {
 toast.error('Не удалось сохранить комментарий');
 }
 };

 // Apply the contextual action of the currently-waking role,
 // logging an event and (for kill roles) eliminating the picked seats,
 // then advancing to the next night action.
 const handleApplyRoleAction = async () => {
 if (!currentNightRole) return;
 const cfg = ROLE_ACTIONS[currentNightRole.name];
 if (!cfg) {
 // Unknown role action — just advance
 await handleNextRole();
 return;
 }
 const { min, max, mode } = resolvePicks(cfg);
 if (mode === 'exact') {
 if (nightTargets.length !== min) {
 toast.error(t('role-action.pick-n', { n: min }));
 return;
 }
 } else {
 if (nightTargets.length < min) {
 toast.error(t('role-action.pick-n', { n: min }));
 return;
 }
 if (max != null && nightTargets.length > max) {
 toast.error(t('role-action.pick-n', { n: max }));
 return;
 }
 }
 try {
 // For all role actions (including kills), log an event and let night
 // resolution happen at switch-phase. This lets heal/immunity/bite/eternal_life
 // that come LATER in the night queue still save the targeted players.
 const actor = game.players.find((p) => p.alive && p.role_id === currentNightRole.id);
 const actorSeat = actor?.seat;
 const actorUserId = actor?.user_id;
 // For Commissioner/Don checks: log correctness so scoring can award the bonus
 let correct;
 if (cfg.eventAction === 'check' || cfg.eventAction === 'don_check') {
 const target = game.players.find((p) => p.seat === nightTargets[0]);
 const targetRole = target?.role_id ? rolesById[target.role_id] : null;
 if (targetRole) {
 correct = cfg.eventAction === 'check'
 ? targetRole.team === 'black'
 : targetRole.name === 'Комиссар';
 }
 }
 await addEvent(id, {
 action: cfg.eventAction,
 actor_seat: actorSeat,
 actor_user_id: actorUserId,
 target_seats: nightTargets,
 correct,
 note: currentNightRole.name,
 });
 setNightTargets([]);
 const g = await nextRole(id);
 setGame(g);
 } catch (e) {
 console.error('apply role action failed', e);
 toast.error('Failed to apply action');
 }
 };

 const handleSkipRoleAction = async () => {
 setNightTargets([]);
 await handleNextRole();
 };

 const handleBack = async () => {
 try {
 const g = await previousStep(id);
 setGame(g);
 } catch (e) {
 toast.error('Failed to go back');
 }
 };

 const handleEnd = async (winners) => {
 try {
 const g = await endGame(id, { winners });
 setGame(g);
 setShowEndModal(false);
 toast.success(`${winners.toUpperCase()} wins`);
 } catch (e) {
 toast.error('Failed to end game');
 }
 };

 const handleRenameSeat = async (seat, newName) => {
 const players = game.players.map((p) => p.seat === seat ? { ...p, name: newName } : p);
 try {
 const g = await updateGame(id, { players });
 setGame(g);
 } catch (e) {
 toast.error('Rename failed');
 }
 };

 const selectedPlayer = selectedSeat ? game.players.find((p) => p.seat === selectedSeat) : null;
 const selectedRole = selectedPlayer?.role_id ? rolesById[selectedPlayer.role_id] : null;

 return (
 <div
 className="min-h-screen pb-8 px-4 md:px-8 bg-[#050505]"
 data-testid="mafia-game-page"
 >
 {/* Top bar */}
 <div className="sticky top-0 z-30 -mx-4 md:-mx-8 px-4 md:px-8 py-3 bg-[#050505]/95 backdrop-blur-md border-b border-white/5">
 <div className="flex items-center justify-between gap-4">
 <button
 onClick={() => navigate('/mafia')}
 data-testid="mafia-game-back"
 className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#DC2626] transition-colors"
 >
 <ArrowLeft size={16} /> {t('game.salon')}
 </button>
 <div className="flex items-center gap-3">
 <PhaseIcon size={18} style={{ color: RED }} />
 <div className="text-right">
 <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
 {t(`game.phase.${phase}`)}
 </p>
 <p className="text-sm text-[#F5F5F5]">{t('common.round')} {game.current_round}</p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 <button
 onClick={() => setShowRoles((v) => !v)}
 data-testid="mafia-toggle-roles"
 className="p-2 rounded-lg border border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-[#DC2626]/40"
 title={t('game.role-visibility')}
 >
 {showRoles ? <Eye size={14} /> : <EyeOff size={14} />}
 </button>
 </div>
 </div>
 </div>

 <div className="max-w-7xl mx-auto pt-4">
 <div className="grid lg:grid-cols-[1fr_360px] gap-6">
 {/* Left: table */}
 <div>
 <div className=" border border-white/5 bg-[#0A0505]/40 p-3">
 <OvalTable
 players={game.players}
 rolesById={rolesById}
 selectedSeat={selectedSeat}
 onSelectSeat={onSeatClick}
 voteTargets={voteTargets}
 nightTargetSeats={nightTargets}
 showRoles={showRoles}
 phase={phase}
 immunitySeats={dayImmunitySeats}
 showImmunityHover={isDay && !isOver}
 onGrantImmunity={handleGrantImmunity}
 />
 </div>

 {/* Phase controls */}
 <div className="mt-4 grid grid-cols-3 gap-2">
 <button
 onClick={handleBack}
 disabled={isSetup}
 data-testid="mafia-phase-back"
 className="flex items-center justify-center gap-2 py-3 border border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-[#DC2626]/40 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
 >
 <ChevronLeft size={16} /> {t('common.back')}
 </button>
 <button
 onClick={handleNextRole}
 disabled={!isNight || !currentNightRole}
 data-testid="mafia-next-role"
 title={t('game.next-role')}
 className="flex items-center justify-center gap-2 py-3 border border-white/15 text-[#F5F5F5] hover:border-[#DC2626]/60 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
 >
 {t('game.next-role')} <ChevronRight size={16} />
 </button>
 <button
 onClick={handlePhaseAction}
 disabled={isOver || (isNight && !!currentNightRole && !isSetup)}
 data-testid="mafia-phase-next"
 title="Switch phase"
 className="flex items-center justify-center gap-2 py-3 text-white font-medium disabled:opacity-40 bg-[#DC2626] hover:bg-[#B91C1C] transition-colors text-sm"
 >
 {isSetup
   ? t('game.start')
   : isDay
     ? t('game.to-night')
     : (isFirstNight ? t('game.wake-day-1') : t('game.to-day'))}
 <ChevronRight size={16} />
 </button>
 </div>

 {/* Action panel */}
 <div className="mt-3 grid grid-cols-2 gap-3">
 {isDay && (
 <button
 onClick={handleEliminateVotes}
 disabled={voteTargets.length === 0}
 data-testid="mafia-bulk-lynch"
 className="flex items-center justify-center gap-2 py-3 border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 disabled:opacity-40"
 >
 <Vote size={16} /> {t('game.lynch')} ({voteTargets.length})
 </button>
 )}
 {isNight && (
 <button
 onClick={handleNightKill}
 disabled={nightTargets.length === 0}
 data-testid="mafia-bulk-night-kill"
 className="flex items-center justify-center gap-2 py-3 border bg-[#DC2626]/10 text-[#DC2626] hover:bg-[#DC2626]/20 disabled:opacity-40"
 style={{ borderColor: 'rgba(220,38,38,0.4)' }}
 >
 <Crosshair size={16} /> {t('game.night-kill')} ({nightTargets.length})
 </button>
 )}
 <button
 onClick={() => setShowEndModal(true)}
 disabled={isOver}
 data-testid="mafia-end-game"
 className="flex items-center justify-center gap-2 py-3 border border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-amber-400/40 disabled:opacity-40"
 >
 <Trophy size={16} /> {t('game.end')}
 </button>
 </div>

 {/* Game over banner */}
 {isOver && (
 <div className="mt-4 p-6 border border-amber-500/30 bg-amber-500/5 text-center">
 <Trophy size={32} className="mx-auto text-amber-400 mb-2" />
 <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1">{t('common.winners')}</p>
 <p className="font-serif text-2xl text-[#F5F5F5] capitalize">
 {tWinner(game.winners)}
 </p>
 </div>
 )}
 </div>

 {/* Right: side panel */}
 <aside className="space-y-4">
 {/* Day phase: Балаган → per-seat speeches → voting */}
 {isDay && dayStage === 'balagan' && (
 <div className="space-y-3">
 <SpeechTimer
 duration={game.balagan_seconds || 120}
 label={t('game.balagan')}
 sublabel={t('game.balagan-sub')}
 accentColor="#F59E0B"
 testid="balagan-timer"
 onComplete={() => {
 toast.success(t('game.balagan-done'));
 if (voteTargets.length > 0) {
 setDayStage('balagan_vote');
 setBalaganVoteIdx(0);
 } else {
 setDayStage('speeches');
 setSpeakerIdx(0);
 }
 }}
 onNext={() => {
 if (voteTargets.length > 0) {
 setDayStage('balagan_vote');
 setBalaganVoteIdx(0);
 } else {
 setDayStage('speeches');
 setSpeakerIdx(0);
 }
 }}
 />
 {/* Nomination dropdowns — propose another player for the day vote */}
 <div
 className="border bg-black/60 p-4 space-y-2.5"
 style={{ borderColor: 'rgba(245,158,11,0.3)' }}
 data-testid="balagan-nominate-panel"
 >
 <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/90">
 {t('game.balagan-nominate-title')}
 </p>
 <div className="grid grid-cols-2 gap-2">
 <label className="block">
 <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
 {t('game.balagan-nominator')}
 </span>
 <select
 value={nominator ?? ''}
 onChange={(e) => setNominator(e.target.value ? Number(e.target.value) : null)}
 data-testid="balagan-nominator-select"
 className="mt-1 w-full bg-black/50 border border-white/15 px-2 py-1.5 text-sm text-[#F5F5F5] outline-none focus:border-amber-400/60"
 >
 <option value="">—</option>
 {alivePlayers.map((p) => (
 <option key={p.seat} value={p.seat}>
 {(p.name && p.name.trim()) || 'Игрок'}
 </option>
 ))}
 </select>
 </label>
 <label className="block">
 <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
 {t('game.balagan-nominee')}
 </span>
 <select
 value={nominee ?? ''}
 onChange={(e) => setNominee(e.target.value ? Number(e.target.value) : null)}
 data-testid="balagan-nominee-select"
 className="mt-1 w-full bg-black/50 border border-white/15 px-2 py-1.5 text-sm text-[#F5F5F5] outline-none focus:border-amber-400/60"
 >
 <option value="">—</option>
 {alivePlayers
 .filter((p) => p.seat !== nominator)
 .map((p) => (
 <option key={p.seat} value={p.seat}>
 {(p.name && p.name.trim()) || 'Игрок'}
 </option>
 ))}
 </select>
 </label>
 </div>
 <button
 onClick={async () => {
 if (nominator == null || nominee == null) {
 toast.error(t('game.balagan-pick-both'));
 return;
 }
 // Add nominee to vote targets (de-duplicated) and log an event
 setVoteTargets((prev) => prev.includes(nominee) ? prev : [...prev, nominee]);
 try {
 await addEvent(id, {
 action: 'nominate',
 actor_seat: nominator,
 target_seats: [nominee],
 note: 'Балаган · номинация',
 });
 const g = await refresh();
 if (g) toast.success(t('game.balagan-nominated', { name: seatName(nominee) }));
 } catch (e) {
 toast.error('Failed to log nomination');
 }
 setNominator(null);
 setNominee(null);
 }}
 disabled={nominator == null || nominee == null}
 data-testid="balagan-nominate-button"
 className="w-full py-2 text-sm font-medium border bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
 style={{ borderColor: 'rgba(245,158,11,0.5)' }}
 >
 {t('game.balagan-nominate-action')}
 </button>
 {voteTargets.length > 0 && (
 <div className="pt-1 border-t border-white/10">
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1.5">
 {t('game.balagan-current-noms')} · {voteTargets.length}
 </p>
 <div className="flex flex-wrap gap-1.5">
 {voteTargets.map((s) => {
 const p = alivePlayers.find((pp) => pp.seat === s);
 return (
 <span
 key={s}
 className="text-xs px-2 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-200"
 data-testid={`balagan-nom-chip-${s}`}
 >
 {(p?.name && p.name.trim()) || 'Игрок'}
 <button
 onClick={() => setVoteTargets((prev) => prev.filter((x) => x !== s))}
 className="ml-1 text-amber-300/70 hover:text-amber-200"
 aria-label="Remove"
 >×</button>
 </span>
 );
 })}
 </div>
 </div>
 )}
 </div>
 </div>
 )}
 {isDay && dayStage === 'speeches' && alivePlayers.length > 0 && (() => {
 const speaker = alivePlayers[Math.min(speakerIdx, alivePlayers.length - 1)];
 const isLast = speakerIdx >= alivePlayers.length - 1;
 const goToVoting = () => {
 setDayStage('voting');
 setCurrentVoterIdx(0);
 setVoteRecords([]);
 };
 return (
 <SpeechTimer
 duration={game.speech_seconds || 60}
 seat={speaker.seat}
 label={t('game.speech')}
 sublabel={`${(speaker.name && speaker.name.trim()) || 'Игрок'} · ${speakerIdx + 1}/${alivePlayers.length}`}
 accentColor="#DC2626"
 testid="speech-timer"
 onComplete={() => {
 if (!isLast) {
 setSpeakerIdx((i) => i + 1);
 } else {
 toast.success(t('game.speeches-done'));
 goToVoting();
 }
 }}
 onNext={() => {
 if (!isLast) {
 setSpeakerIdx((i) => i + 1);
 } else {
 goToVoting();
 }
 }}
 />
 );
 })()}
 {/* Balagan immediate vote: 50%+ yes/no per Балаган-nominee */}
 {isDay && dayStage === 'balagan_vote' && voteTargets.length > 0 && (() => {
 const seat = voteTargets[balaganVoteIdx];
 const target = (game.players || []).find((p) => p.seat === seat);
 if (!target) return null;
 const aliveCount = alivePlayers.length;
 const threshold = Math.floor(aliveCount / 2) + 1;
 const isLast = balaganVoteIdx >= voteTargets.length - 1;
 const advance = () => {
 if (!isLast) {
 setBalaganVoteIdx((i) => i + 1);
 } else {
 setVoteTargets([]);
 setDayStage('speeches');
 setSpeakerIdx(0);
 }
 };
 return (
 <div
 className="border bg-black/60 p-4 space-y-3"
 style={{ borderColor: 'rgba(245,158,11,0.6)' }}
 data-testid="balagan-vote-panel"
 >
 <div className="flex items-center justify-between">
 <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
 {t('game.balagan-vote-title')} · {balaganVoteIdx + 1}/{voteTargets.length}
 </p>
 <span className="text-[10px] text-amber-300/70">
 {t('game.balagan-vote-threshold', { n: threshold, total: aliveCount })}
 </span>
 </div>
 <div className="flex items-center gap-3 py-2">
 <div className="w-12 h-12 rounded-md flex items-center justify-center bg-amber-500/10 border border-amber-500/40">
 {(() => {
 const r = target.role_id ? rolesById[target.role_id] : null;
 return r ? <span className="text-2xl">{r.icon}</span> : <span className="text-amber-300 text-xs">—</span>;
 })()}
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-lg font-serif text-[#F5F5F5] truncate">{(target.name && target.name.trim()) || 'Игрок'}</p>
 <p className="text-xs text-[#A3A3A3]">{t('game.balagan-vote-question')}</p>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2">
 <button
 onClick={async () => {
 try {
 await addEvent(id, {
 action: 'balagan_vote',
 target_seats: [seat],
 note: 'Изгнан (>50%)',
 correct: true,
 });
 await addEvent(id, {
 action: 'day_lynch',
 target_seats: [seat],
 note: 'Балаган-голосование',
 });
 await handleEliminate(seat, 'day_lynch');
 } catch (e) {
 toast.error('Failed to lynch');
 }
 advance();
 }}
 data-testid="balagan-vote-kill"
 className="py-3 text-sm font-medium border bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 transition-colors"
 style={{ borderColor: 'rgba(244,63,94,0.5)' }}
 >
 {t('game.balagan-vote-kill')}
 </button>
 <button
 onClick={async () => {
 try {
 await addEvent(id, {
 action: 'balagan_vote',
 target_seats: [seat],
 note: 'Оставлен (<50%)',
 correct: false,
 });
 await refresh();
 } catch (e) { /* non-fatal */ }
 advance();
 }}
 data-testid="balagan-vote-save"
 className="py-3 text-sm font-medium border border-white/15 text-[#F5F5F5] hover:border-white/30 transition-colors"
 >
 {t('game.balagan-vote-save')}
 </button>
 </div>
 </div>
 );
 })()}

 {/* Normal voting: per-voter UI */}
 {isDay && dayStage === 'voting' && alivePlayers.length > 0 && (() => {
 const voter = alivePlayers[Math.min(currentVoterIdx, alivePlayers.length - 1)];
 const isLastVoter = currentVoterIdx >= alivePlayers.length - 1;
 // All alive players are candidates — including the voter themselves.
 // Voting against yourself counts as 2 votes (see tally below).
 const candidates = alivePlayers;
 const recordVote = async (targetSeat) => {
 setVoteRecords((prev) => [...prev, { voter_seat: voter.seat, target_seat: targetSeat }]);
 // Persist each cast as an event so reports stay accurate
 const isSelfVote = targetSeat != null && targetSeat === voter.seat;
 try {
 await addEvent(id, {
 action: 'voting_cast',
 actor_seat: voter.seat,
 target_seats: targetSeat == null ? [] : [targetSeat],
 note: targetSeat == null
 ? 'Воздержался'
 : isSelfVote
 ? 'Голос против себя ×2'
 : `Голос за ${seatName(targetSeat)}`,
 });
 } catch (e) { /* non-fatal — UI flow continues */ }
 if (!isLastVoter) {
 setCurrentVoterIdx((i) => i + 1);
 } else {
 setDayStage('voting_results');
 }
 };
 return (
 <div
 className="border bg-black/60 p-4 space-y-3"
 style={{ borderColor: 'rgba(220,38,38,0.5)' }}
 data-testid="voting-stage-card"
 >
 <SpeechTimer
 key={`vote-${currentVoterIdx}`}
 duration={game.vote_seconds || 5}
 label={t('game.voting-stage')}
 sublabel={`${(voter.name && voter.name.trim()) || 'Игрок'} · ${currentVoterIdx + 1}/${alivePlayers.length}`}
 seat={voter.seat}
 accentColor="#DC2626"
 autoStart
 testid="voting-timer"
 onComplete={() => {
 toast(t('game.voting-tick-abstain'));
 }}
 />
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
 {t('game.voting-pick-target')}
 </p>
 <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
 {candidates.map((c) => (
 <button
 key={c.seat}
 onClick={() => recordVote(c.seat)}
 data-testid={`voting-pick-${c.seat}`}
 className="px-3 py-2 text-left text-sm border border-white/15 hover:border-rose-500/50 hover:bg-rose-500/10 transition-colors"
 >
 <span className="text-[#F5F5F5] truncate">{(c.name && c.name.trim()) || 'Игрок'}</span>
 </button>
 ))}
 </div>
 <button
 onClick={() => recordVote(null)}
 data-testid="voting-abstain"
 className="w-full py-2 text-sm border border-white/10 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-white/30"
 >
 {t('game.voting-abstain')}
 </button>
 </div>
 );
 })()}

 {/* Voting results — tally + apply lynch */}
 {isDay && dayStage === 'voting_results' && (() => {
 const tally = new Map();
 // Self-vote counts as 2 votes against the voter; normal votes count as 1.
 voteRecords.forEach((r) => {
 if (r.target_seat == null) return;
 const weight = r.target_seat === r.voter_seat ? 2 : 1;
 tally.set(r.target_seat, (tally.get(r.target_seat) || 0) + weight);
 });
 const sorted = Array.from(tally.entries()).sort((a, b) => b[1] - a[1]);
 const topCount = sorted.length > 0 ? sorted[0][1] : 0;
 const winners = sorted.filter(([, c]) => c === topCount).map(([s]) => s);
 const abstain = voteRecords.filter((r) => r.target_seat == null).length;
 const isTie = winners.length > 1;
 return (
 <div
 className="border bg-black/60 p-4 space-y-3"
 style={{ borderColor: 'rgba(220,38,38,0.5)' }}
 data-testid="voting-results-card"
 >
 <p className="text-[10px] uppercase tracking-[0.3em] text-[#DC2626]">
 {t('game.voting-results-title')}
 </p>

 {sorted.length === 0 ? (
 <p className="text-sm text-[#A3A3A3] italic">{t('game.voting-no-votes')}</p>
 ) : (
 <ul className="space-y-1.5">
 {sorted.map(([seat, count]) => {
 const p = (game.players || []).find((pp) => pp.seat === seat);
 const isWinner = winners.includes(seat);
 const pct = alivePlayers.length > 0 ? (count / alivePlayers.length) * 100 : 0;
 return (
 <li
 key={seat}
 className="space-y-1"
 data-testid={`voting-tally-${seat}`}
 >
 <div className="flex items-center gap-2 text-sm">
 <span className={`font-medium truncate flex-1 ${isWinner ? 'text-rose-300' : 'text-[#F5F5F5]'}`}>
 {(p?.name && p.name.trim()) || 'Игрок'}
 </span>
 <span className={`font-mono tabular-nums text-sm ${isWinner ? 'text-rose-300' : 'text-[#F5F5F5]'}`}>
 {count}
 </span>
 </div>
 <div className="h-1.5 bg-white/5 overflow-hidden">
 <div
 className="h-full"
 style={{
 width: `${pct}%`,
 background: isWinner ? '#DC2626' : '#52525B',
 transition: 'width 0.4s ease',
 }}
 />
 </div>
 </li>
 );
 })}
 </ul>
 )}
 {abstain > 0 && (
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
 {t('game.voting-abstained', { n: abstain })}
 </p>
 )}

 {isTie && winners.length > 0 && (
 <p className="text-xs text-amber-300 italic">{t('game.voting-tie')}</p>
 )}

 <div className="flex gap-2 pt-1">
 <button
 onClick={async () => {
 if (winners.length === 0) {
 toast.error(t('game.voting-no-votes'));
 return;
 }
 try {
 // Persist a structured voting_result event with the full tally
 const tallyArr = Array.from(tally.entries()).map(([s, c]) => ({ seat: s, count: c }));
 await addEvent(id, {
 action: 'voting_result',
 target_seats: winners,
 note: `Голосование · ${topCount}/${alivePlayers.length} голосов | ${tallyArr.map(({ seat: s, count: c }) => `${seatName(s)}:${c}`).join(' · ')}${abstain ? ` | возд.${abstain}` : ''}`,
 });
 for (const w of winners) {
 // eslint-disable-next-line no-await-in-loop
 await addEvent(id, {
 action: 'day_lynch',
 target_seats: [w],
 note: `Голосование · ${topCount}/${alivePlayers.length}`,
 });
 // eslint-disable-next-line no-await-in-loop
 await handleEliminate(w, 'day_lynch');
 }
 } catch (e) {
 toast.error('Failed to apply lynch');
 }
 }}
 disabled={winners.length === 0}
 data-testid="voting-apply-lynch"
 className="flex-1 py-2.5 text-sm font-medium bg-rose-500/20 text-rose-200 border border-rose-500/50 hover:bg-rose-500/30 disabled:opacity-40 transition-colors"
 >
 {isTie ? t('game.voting-apply-tie', { n: winners.length }) : t('game.lynch')}
 {winners.length > 0 && ` · ${winners.map((s) => seatName(s)).join(', ')}`}
 </button>
 <button
 onClick={() => {
 // Skip lynch — keep everyone, move on. Host can still switch to night.
 setDayStage('balagan');
 setSpeakerIdx(0);
 setVoteRecords([]);
 }}
 className="px-3 py-2.5 text-xs text-[#A3A3A3] border border-white/10 hover:text-[#F5F5F5] hover:border-white/30"
 data-testid="voting-skip-lynch"
 >
 {t('game.voting-skip')}
 </button>
 </div>
 </div>
 );
 })()}

 {/* Night phase keeps the speech timer for free-form moderator use */}
 {isNight && (
 <SpeechTimer
 duration={game.speech_seconds || 60}
 label={t('game.speech')}
 sublabel={selectedSeat !== null ? `Seat ${selectedSeat}` : t('common.timer')}
 seat={selectedSeat}
 accentColor="#DC2626"
 />
 )}

 {/* Team status */}
 <div className=" border border-white/5 bg-[#0A0505]/60 p-4">
 <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] mb-3">{t('game.alive')}</p>
 <div className="grid grid-cols-3 gap-2 text-center">
 <div className="rounded-lg p-2 border border-rose-500/30 bg-rose-500/5">
 <p className="text-2xl font-serif text-rose-300">{teamCounts.red}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{t('game.civ-short')}</p>
 </div>
 <div className="rounded-lg p-2 border border-zinc-500/40 bg-zinc-700/20">
 <p className="text-2xl font-serif text-zinc-200">{teamCounts.black}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{t('game.mafia-short')}</p>
 </div>
 <div className="rounded-lg p-2 border border-violet-500/30 bg-violet-500/5">
 <p className="text-2xl font-serif text-violet-300">{teamCounts.gray}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">{t('game.grey-short')}</p>
 </div>
 </div>
 </div>

 {/* Night Report — appears when host has applied every role on the queue */}
 {isNight && nightDone && (
 <div
 className=" border p-4 space-y-3"
 style={{ borderColor: 'rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.07)' }}
 data-testid="mafia-night-report"
 >
 <div className="flex items-center justify-between">
 <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
 {isFirstNight ? t('game.report-first-night') : t('game.report-night')} {game.current_round}
 </p>
 <span className="text-[10px] text-amber-300/70">{t('game.report-ready')}</span>
 </div>

 <div>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1.5">
 {t('game.report-deaths')}
 </p>
 {nightReport.deaths.length === 0 ? (
 <p className="text-sm text-emerald-300/90">{t('game.report-no-deaths')}</p>
 ) : (
 <ul className="space-y-1">
 {nightReport.deaths.map((d, i) => (
 <li
 key={`${d.seat}-${i}`}
 className="flex items-center gap-2 text-sm text-[#F5F5F5]"
 data-testid={`night-report-death-${d.seat}`}
 >
 <Skull size={13} className="text-rose-400 shrink-0" />
 <span className="text-[#F5F5F5]/95 truncate">{d.name}</span>
 {d.role && (
 <span className="text-[11px] text-[#A3A3A3] ml-auto truncate">
 {d.role.icon} {tRole(d.role.name)}
 </span>
 )}
 </li>
 ))}
 </ul>
 )}
 </div>

 {/* Saved / Under Protection — every player who received heal/immunity/bite/eternal_life */}
 {nightReport.saved && nightReport.saved.length > 0 && (
 <div className="pt-2 border-t border-white/10">
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-1.5">
 {t('game.report-immunity')}
 </p>
 <ul className="space-y-1">
 {nightReport.saved.map((s) => (
 <li
 key={`saved-${s.seat}`}
 className="flex items-start gap-2 text-sm"
 data-testid={`night-report-saved-${s.seat}`}
 >
 <Shield
 size={13}
 className={`shrink-0 mt-0.5 ${s.savedFromKill ? 'text-amber-300' : 'text-emerald-300'}`}
 />
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-1.5">
 <span className="font-medium text-[#F5F5F5] truncate">{s.name}</span>
 {s.role && (
 <span className="text-[10px] text-[#A3A3A3] ml-auto truncate">
 {s.role.icon} {tRole(s.role.name)}
 </span>
 )}
 </div>
 <div className="flex flex-wrap gap-1 mt-0.5">
 {s.protections.map((p, pi) => (
 <span
 key={`${s.seat}-prot-${pi}`}
 className="text-[10px] px-1.5 py-0.5 border rounded"
 style={{
 borderColor: s.savedFromKill ? 'rgba(245,158,11,0.5)' : 'rgba(16,185,129,0.4)',
 background: s.savedFromKill ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.08)',
 color: s.savedFromKill ? '#FCD34D' : '#6EE7B7',
 }}
 >
 {p.name}{p.source !== '—' && ` · ${p.source}`}
 </span>
 ))}
 {s.savedFromKill && (
 <span
 className="text-[10px] px-1.5 py-0.5 border rounded font-medium"
 style={{
 borderColor: 'rgba(244,63,94,0.5)',
 background: 'rgba(244,63,94,0.1)',
 color: '#FCA5A5',
 }}
 >
 спасён от убийства
 </span>
 )}
 </div>
 </div>
 </li>
 ))}
 </ul>
 </div>
 )}

 {nightReport.actions.length > 0 && (
 <div className="pt-2 border-t border-white/10">
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">
 {t('game.report-actions')}
 </p>
 <ul className="space-y-2">
 {nightReport.actions.map((ev) => (
 <li
 key={ev.id}
 className={`rounded-md border bg-black/30 px-2.5 py-2 space-y-1 ${ev.voided ? 'opacity-50' : ''}`}
 style={{ borderColor: ev.voided ? 'rgba(244,63,94,0.4)' : 'rgba(255,255,255,0.1)' }}
 data-testid={`night-report-action-${ev.action}`}
 >
 {/* Actor row: role icon + role name + actor seat/name */}
 <div className="flex items-baseline gap-1.5 text-xs">
 {ev.actorRole?.icon && (
 <span className="text-sm leading-none">{ev.actorRole.icon}</span>
 )}
 <span className={`font-medium text-[#F5F5F5] ${ev.voided ? 'line-through' : ''}`}>
 {ev.actorRole?.name ? tRole(ev.actorRole.name) : ev.action}
 </span>
 {ev.actor && (
 <span className="text-[10px] text-[#A3A3A3]">
 {ev.actor.name && ev.actor.name !== 'Игрок' ? ev.actor.name : ''}
 </span>
 )}
 <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-300/80">
 {ev.verb}
 </span>
 </div>
 {/* Target rows: seat + name + role + outcome */}
 {ev.targets.length === 0 ? (
 <p className="text-[11px] text-[#A3A3A3] italic pl-1">—</p>
 ) : (
 <ul className="pl-1 space-y-0.5">
 {ev.targets.map((tg, i) => (
 <li
 key={`${ev.id}-${tg.seat}-${i}`}
 className="flex items-center gap-1.5 text-xs"
 >
 <span className="text-[#A3A3A3]">→</span>
 <span className="font-medium text-[#F5F5F5] truncate max-w-[8rem]">{tg.name}</span>
 {tg.role && showRoles && (
 <span className="text-[10px] text-[#A3A3A3]">
 {tg.role.icon} {tRole(tg.role.name)}
 </span>
 )}
 {tg.outcome && (
 <span
 className="ml-auto text-[10px] px-1.5 py-0.5 rounded border"
 style={{
 borderColor: tg.willDie ? 'rgba(244,63,94,0.5)' : 'rgba(16,185,129,0.4)',
 background: tg.willDie ? 'rgba(244,63,94,0.1)' : 'rgba(16,185,129,0.08)',
 color: tg.willDie ? '#FCA5A5' : '#6EE7B7',
 }}
 >
 {tg.outcome}
 </span>
 )}
 </li>
 ))}
 </ul>
 )}
 {/* Host comment line (shown if present) */}
 {ev.hostComment && (
 <p className="text-[11px] text-amber-300/90 italic pl-1" data-testid={`event-comment-${ev.id}`}>
 💬 {ev.hostComment}
 </p>
 )}
 {/* Host controls — void, override, comment */}
 <div className="flex items-center gap-1 pt-1 border-t border-white/5 mt-1">
 <button
 onClick={() => patchEventLocal(ev.id, { voided: !ev.voided })}
 data-testid={`event-void-${ev.id}`}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-rose-500/50 hover:text-rose-300 text-[#A3A3A3] transition-colors"
 title={ev.voided ? 'Восстановить событие' : 'Отменить событие'}
 >
 {ev.voided ? '↺ Восст.' : '× Отменить'}
 </button>
 <button
 onClick={() => {
 const v = window.prompt('Изменить результат (оставьте пустым, чтобы сбросить)', ev.hostOverride || '');
 if (v !== null) patchEventLocal(ev.id, { host_override: v });
 }}
 data-testid={`event-override-${ev.id}`}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-amber-500/50 hover:text-amber-300 text-[#A3A3A3] transition-colors"
 title="Изменить результат"
 >
 ✎ Результат
 </button>
 <button
 onClick={() => {
 const v = window.prompt('Комментарий хоста', ev.hostComment || '');
 if (v !== null) patchEventLocal(ev.id, { host_comment: v });
 }}
 data-testid={`event-comment-btn-${ev.id}`}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-blue-500/50 hover:text-blue-300 text-[#A3A3A3] transition-colors"
 title="Добавить комментарий"
 >
 💬 Комм.
 </button>
 </div>
 </li>
 ))}
 </ul>
 </div>
 )}

 <div className="pt-2 border-t border-white/10 grid grid-cols-3 gap-2 text-center">
 <div>
 <p className="text-lg font-serif text-[#F5F5F5]">{alivePlayers.length}</p>
 <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">{t('game.alive')}</p>
 </div>
 <div>
 <p className="text-lg font-serif text-rose-300">{nightReport.deaths.length}</p>
 <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">{t('game.report-night-deaths-short')}</p>
 </div>
 <div>
 <p className="text-lg font-serif text-[#A3A3A3]">{deadPlayers.length}</p>
 <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">{t('game.eliminated')}</p>
 </div>
 </div>

 <button
 onClick={handlePhaseAction}
 data-testid="mafia-night-report-wake"
 className="w-full py-2.5 text-white font-medium bg-[#DC2626] hover:bg-[#B91C1C] transition-colors flex items-center justify-center gap-2"
 >
 <Sun size={16} />
 {isFirstNight ? t('game.wake-day-1') : t('game.to-day')}
 </button>
 </div>
 )}

 {/* Night context — per-role action form */}
 {isNight && !nightDone && currentNightRole && (() => {
 const cfg = ROLE_ACTIONS[currentNightRole.name];
 const { min, max, mode } = resolvePicks(cfg);
 const canApply = cfg && (
   mode === 'exact'
     ? nightTargets.length === min
     : nightTargets.length >= min && (max == null || nightTargets.length <= max)
 );
 return (
 <div
 className=" border p-4 space-y-3"
 style={{ borderColor: 'rgba(220,38,38,0.4)', background: 'rgba(220,38,38,0.06)' }}
 data-testid="mafia-role-action-panel"
 >
 <div className="flex items-center justify-between">
 <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: RED }}>
 {t('game.currently-waking')}
 </p>
 <p className="text-[10px] text-[#A3A3A3]">
 {t('game.step')} {(game.night_action_index || 0) + 1} / {(game.night_queue || []).length}
 </p>
 </div>

 <div className="flex items-start gap-3">
 <span className="text-3xl">{currentNightRole.icon}</span>
 <div className="min-w-0 flex-1">
 <p className="font-serif text-lg text-[#F5F5F5]">
 {tRole(currentNightRole.name)}
 {(() => {
 const actor = (game.players || []).find((p) => p.alive && p.role_id === currentNightRole.id);
 if (!actor) return null;
 return (
 <span className="ml-2 text-sm font-normal text-amber-200/90" data-testid="night-action-actor">
 · {(actor.name && actor.name.trim()) || 'Игрок'}
 </span>
 );
 })()}
 </p>
 <p className="text-xs text-[#A3A3A3] leading-relaxed mt-1">
 {tRoleDesc(currentNightRole.name, currentNightRole.description)}
 </p>
 </div>
 </div>

 {cfg && (
 <>
 {/* Context notes */}
 <div className="flex flex-wrap gap-1.5">
 {cfg.firstNightOnly && (
 <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] uppercase tracking-wider border border-amber-500/30">
 {t('role-action.first-night-only')}
 </span>
 )}
 {cfg.thirdNightOnly && (
 <span className="px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 text-[10px] uppercase tracking-wider border border-violet-500/30">
 {t('role-action.third-night-only')}
 </span>
 )}
 {cfg.pickFromDead && (
 <span className="px-2 py-0.5 rounded-full bg-zinc-500/15 text-zinc-300 text-[10px] uppercase tracking-wider border border-zinc-500/40">
 {t('role-action.target-dead')}
 </span>
 )}
 </div>

 {/* Selection indicator */}
 <div className="flex items-center gap-2 text-xs flex-wrap">
 <span className="text-[#A3A3A3]">{t('role-action.pick')}:</span>
 {mode === 'exact' ? (
   Array.from({ length: min }).map((_, i) => (
 <span
 key={i}
 className="w-7 h-7 rounded-md border flex items-center justify-center font-serif text-sm"
 style={{
 borderColor: nightTargets[i] ? RED : 'rgba(255,255,255,0.15)',
 background: nightTargets[i] ? 'rgba(220,38,38,0.18)' : 'transparent',
 color: nightTargets[i] ? '#F5F5F5' : '#525252',
 }}
 data-testid={`mafia-pick-slot-${i}`}
 >
 {nightTargets[i] || '—'}
 </span>
   ))
 ) : (
   <>
     {nightTargets.length === 0 ? (
       <span className="text-[#525252] italic">—</span>
     ) : (
       nightTargets.map((seat) => (
         <span
           key={seat}
           className="min-w-7 h-7 px-2 rounded-md border flex items-center justify-center font-serif text-sm"
           style={{ borderColor: RED, background: 'rgba(220,38,38,0.18)', color: '#F5F5F5' }}
           data-testid={`mafia-pick-chip-${seat}`}
         >
           {seat}
         </span>
       ))
     )}
     <span className="text-[10px] text-[#A3A3A3] ml-auto">
       {nightTargets.length}/{max == null ? '∞' : max} · мин {min}
     </span>
   </>
 )}
 </div>

 {/* Apply / Skip */}
 <div className="grid grid-cols-2 gap-2 pt-1">
 <button
 onClick={handleApplyRoleAction}
 disabled={!canApply}
 data-testid="mafia-role-apply"
 className="py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-40 bg-[#DC2626] hover:bg-[#B91C1C] transition-colors"
 >
 {t(cfg.actionKey)}
 </button>
 <button
 onClick={handleSkipRoleAction}
 data-testid="mafia-role-skip"
 className="py-2.5 rounded-lg border border-white/15 text-[#A3A3A3] hover:text-[#F5F5F5] text-sm"
 >
 {t('role-action.skip')}
 </button>
 </div>
 </>
 )}

 {/* Per-role configured action buttons (from role settings) */}
 {(currentNightRole.actions || []).length > 0 && (
   <div className="pt-2 mt-2 border-t border-white/10 space-y-2">
     <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Actions</p>
     <div className="flex flex-wrap gap-1.5">
       {(currentNightRole.actions || []).map((label) => {
         const isKill = label === 'Убить' || /kill/i.test(label);
         const isImmunity = label === 'Иммунитет' || /immun/i.test(label);
         const hasTarget = selectedSeat !== null;
         return (
           <button
             key={label}
             onClick={async () => {
               try {
                 await addEvent(id, {
                   action: isKill ? 'night_kill' : isImmunity ? 'immunity' : 'role_action',
                   target_seats: hasTarget ? [selectedSeat] : (nightTargets.length ? nightTargets : []),
                   note: `${currentNightRole.name}: ${label}${hasTarget ? ` → seat ${selectedSeat}` : ''}`,
                 });
                 if (isKill && hasTarget) {
                   await handleEliminate(selectedSeat, 'night_kill');
                 }
                 toast.success(`${label} logged`);
               } catch (e) { toast.error('Action failed'); }
             }}
             data-testid={`role-action-btn-${label}`}
             className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
               isKill ? 'border-rose-500/60 text-rose-300 hover:bg-rose-500 hover:text-white'
               : isImmunity ? 'border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-white'
               : 'border-white/15 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-white/40'
             }`}
             style={{ borderRadius: '6px' }}
           >
             {label}
           </button>
         );
       })}
     </div>
     <p className="text-[10px] text-[#6B7280]">Select a seat on the table first, then tap an action.</p>
   </div>
 )}
 </div>
 );
 })()}

 {/* Selected player details */}
 {selectedPlayer && (
 <div className=" border border-white/10 bg-[#0A0505]/60 p-4" data-testid="mafia-selected-panel">
 <div className="flex items-center justify-between mb-3">
 <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">{(selectedPlayer.name && selectedPlayer.name.trim()) || 'Игрок'}</p>
 <button onClick={() => setSelectedSeat(null)} className="p-1 rounded hover:bg-white/5"><X size={14} className="text-[#A3A3A3]" /></button>
 </div>
 <input
 defaultValue={selectedPlayer.name}
 onBlur={(e) => {
 const v = e.target.value.trim();
 if (v && v !== selectedPlayer.name) handleRenameSeat(selectedPlayer.seat, v);
 }}
 className="w-full px-2 py-1.5 rounded-lg bg-black/60 border border-white/10 text-[#F5F5F5] text-sm outline-none focus:border-[#DC2626]/50"
 />
 {selectedRole && (
 <div className="mt-3 flex items-center gap-2">
 <span className="text-2xl">{selectedRole.icon}</span>
 <div>
 <p className="text-sm text-[#F5F5F5]">{tRole(selectedRole.name)}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
 {selectedRole.team === 'red' ? t('roles.civilians') : selectedRole.team === 'black' ? t('roles.mafia') : t('roles.neutral')}
 </p>
 </div>
 </div>
 )}
 <div className="mt-3 grid grid-cols-2 gap-2">
 {selectedPlayer.alive ? (
 <>
 <button
 onClick={() => handleEliminate(selectedPlayer.seat, 'day_lynch')}
 data-testid="mafia-eliminate-votes"
 className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs"
 >
 <Vote size={12} /> {t('game.lynch')}
 </button>
 <button
 onClick={() => handleEliminate(selectedPlayer.seat, 'night_kill')}
 data-testid="mafia-night-kill"
 className="flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs"
 style={{ borderColor: 'rgba(220,38,38,0.4)', color: RED }}
 >
 <Crosshair size={12} /> {t('game.kill')}
 </button>
 </>
 ) : (
 <button
 onClick={() => handleRevive(selectedPlayer.seat)}
 data-testid="mafia-revive-btn"
 className="col-span-2 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs"
 >
 <Heart size={12} /> {t('game.restore')}
 </button>
 )}
 </div>
 </div>
 )}

 {/* Event log */}
 <div className=" border border-white/5 bg-[#0A0505]/60 p-4">
 <div className="flex items-center justify-between mb-3">
 <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] flex items-center gap-2">
 <FileText size={11} /> {t('game.event-log')}
 </p>
 <span className="text-[10px] text-[#A3A3A3]">{(game.events || []).length}</span>
 </div>

 {/* Quick links: open per-round report in a modal */}
 {(() => {
 const rounds = Array.from(new Set((game.events || []).map((e) => `${e.round}|${e.phase}`)))
 .map((k) => { const [r, p] = k.split('|'); return { round: Number(r), phase: p }; })
 // Day comes first within a round (Day N → Night N), then the next round starts.
 .sort((a, b) => a.round - b.round || (a.phase === 'day' ? -1 : 1));
 if (rounds.length === 0) return null;
 return (
 <div className="mb-3 flex flex-wrap gap-1.5">
 {rounds.map(({ round: r, phase: ph }) => {
 const isNightPh = ph === 'night' || ph === 'first_night';
 return (
 <button
 key={`${r}-${ph}`}
 onClick={() => setReportModal({ round: r, phase: ph })}
 data-testid={`event-log-report-link-${ph}-${r}`}
 className="text-[10px] px-2 py-1 border transition-colors hover:bg-white/5"
 style={{
 borderColor: isNightPh ? 'rgba(245,158,11,0.4)' : 'rgba(220,38,38,0.4)',
 color: isNightPh ? '#FCD34D' : '#FCA5A5',
 }}
 >
 {isNightPh ? '🌙' : '☀️'} {isNightPh ? t('game.report-night') : t('game.report-day')} {r}
 </button>
 );
 })}
 </div>
 );
 })()}

 <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="mafia-event-log">
 {(game.events || []).slice().reverse().map((ev) => (
 <div key={ev.id} className="text-xs text-[#A3A3A3] border-l-2 pl-2 py-0.5" style={{ borderColor: ev.action.includes('kill') || ev.action.includes('lynch') ? RED : '#52525B' }}>
 <p className="text-[#F5F5F5]">
 <span className="text-[10px] uppercase tracking-wider" style={{ color: RED }}>
 R{ev.round} · {t(`game.phase.${ev.phase}`)}
 </span>
 </p>
 <p className="mt-0.5">
 {(() => {
 const targets = (ev.target_seats || []).map((s) => seatName(s)).join(', ') || '—';
 const actorName = ev.actor_seat != null ? seatName(ev.actor_seat) : '—';
 if (ev.action === 'day_lynch') return `${t('game.lynch')} · ${targets}`;
 if (ev.action === 'night_kill') return `${t('game.night-kill')} · ${targets}${ev.note ? ` · ${ev.note}` : ''}`;
 if (ev.action === 'night_save') return `Спасён · ${targets}${ev.note ? ` · ${ev.note}` : ''}`;
 if (ev.action === 'night_action') return `${ev.note || ''} → ${targets}`;
 if (ev.action === 'balagan_vote') return `Балаган · ${targets} · ${ev.note || ''}`;
 if (ev.action === 'voting_cast') return `Голос · ${actorName} → ${ev.target_seats?.length ? targets : 'воздержался'}`;
 if (ev.action === 'voting_result') return `Итог голосования · ${targets} · ${ev.note || ''}`;
 const text = (ev.note || ev.action) + (ev.target_seats?.length ? ` → ${targets}` : '');
 return text;
 })()}
 </p>
 </div>
 ))}
 {(game.events || []).length === 0 && (
 <p className="text-xs text-[#A3A3A3]/60 italic">{t('game.no-events')}</p>
 )}
 </div>
 </div>

 {/* Dead players */}
 {deadPlayers.length > 0 && (
 <div className=" border border-white/5 bg-[#0A0505]/60 p-4">
 <p className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3] mb-3 flex items-center gap-2">
 <Skull size={11} /> {t('game.eliminated')}
 </p>
 <div className="space-y-1.5">
 {deadPlayers.map((p) => {
 const r = rolesById[p.role_id];
 return (
 <button
 key={p.id}
 onClick={() => setSelectedSeat(p.seat)}
 className="w-full flex items-center justify-between text-xs px-2 py-1.5 rounded-lg hover:bg-white/5"
 >
 <span className="text-[#A3A3A3] line-through">{(p.name && p.name.trim()) || 'Игрок'}</span>
 <span className="text-[#A3A3A3]/70">{r ? r.icon : ''} R{p.eliminated_round}</span>
 </button>
 );
 })}
 </div>
 </div>
 )}
 </aside>
 </div>
 </div>

 {/* End game modal */}
 {showEndModal && (
 <div
 className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
 onClick={() => setShowEndModal(false)}
 >
 <motion.div
 initial={{ y: 20, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 onClick={(e) => e.stopPropagation()}
 className="w-full max-w-md bg-[#0A0505] border border-white/10 p-6"
 data-testid="mafia-end-modal"
 >
 <h2 className="font-serif text-2xl text-[#F5F5F5] mb-1">{t('game.declare-winners')}</h2>
 <p className="text-sm text-[#A3A3A3] mb-6">{t('game.who-won')}</p>
 <div className="grid grid-cols-3 gap-2">
 <button
 onClick={() => handleEnd('red')}
 data-testid="mafia-end-red"
 className="p-4 border border-rose-500/30 bg-rose-500/5 hover:bg-rose-500/15"
 >
 <p className="font-serif text-lg text-rose-300">{tWinner('red')}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-1">Red</p>
 </button>
 <button
 onClick={() => handleEnd('black')}
 data-testid="mafia-end-black"
 className="p-4 border border-zinc-500/40 bg-zinc-700/20 hover:bg-zinc-700/30"
 >
 <p className="font-serif text-lg text-zinc-200">{tWinner('black')}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-1">Black</p>
 </button>
 <button
 onClick={() => handleEnd('gray')}
 data-testid="mafia-end-gray"
 className="p-4 border border-violet-500/30 bg-violet-500/5 hover:bg-violet-500/15"
 >
 <p className="font-serif text-lg text-violet-300">{tWinner('gray')}</p>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-1">Grey</p>
 </button>
 </div>
 <button
 onClick={() => setShowEndModal(false)}
 className="w-full mt-4 py-2.5 rounded-lg border border-white/10 text-[#A3A3A3]"
 >
 {t('common.cancel')}
 </button>
 </motion.div>
 </div>
 )}

 {/* Per-round Report popup */}
 {reportModal && (() => {
 const rpt = buildReport(reportModal.round, reportModal.phase);
 const isNightPh = reportModal.phase === 'night' || reportModal.phase === 'first_night';
 return (
 <div
 className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
 onClick={() => setReportModal(null)}
 data-testid="report-modal"
 >
 <motion.div
 initial={{ y: 20, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 onClick={(e) => e.stopPropagation()}
 className="w-full max-w-lg max-h-[85vh] overflow-y-auto bg-[#0A0505] border p-6 space-y-4"
 style={{ borderColor: isNightPh ? 'rgba(245,158,11,0.5)' : 'rgba(220,38,38,0.5)' }}
 >
 <div className="flex items-center justify-between">
 <h2 className="font-serif text-xl text-[#F5F5F5]">
 {isNightPh ? '🌙' : '☀️'} {isNightPh ? t('game.report-night') : t('game.report-day')} {reportModal.round}
 </h2>
 <button
 onClick={() => setReportModal(null)}
 className="p-1 hover:bg-white/5"
 data-testid="report-modal-close"
 >
 <X size={18} className="text-[#A3A3A3]" />
 </button>
 </div>

 <div>
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">
 {isNightPh ? t('game.report-deaths') : 'Линчёваны'}
 </p>
 {rpt.deaths.length === 0 ? (
 <p className="text-sm text-emerald-300/90">{isNightPh ? t('game.report-no-deaths') : 'Никого не линчевали'}</p>
 ) : (
 <ul className="space-y-1">
 {rpt.deaths.map((d, i) => (
 <li key={`${d.seat}-${i}`} className="flex items-center gap-2 text-sm text-[#F5F5F5]">
 <Skull size={13} className="text-rose-400 shrink-0" />
 <span className="text-[#F5F5F5]/95 truncate">{d.name}</span>
 {d.role && (
 <span className="text-[11px] text-[#A3A3A3] ml-auto truncate">
 {d.role.icon} {tRole(d.role.name)}
 </span>
 )}
 </li>
 ))}
 </ul>
 )}
 </div>

 {rpt.actions.length > 0 && (
 <div className="pt-3 border-t border-white/10">
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">
 {isNightPh ? t('game.report-actions') : 'Дневные действия'}
 </p>
 <ul className="space-y-2">
 {rpt.actions.map((ev) => (
 <li
 key={ev.id}
 className={`rounded-md border bg-black/30 px-2.5 py-2 space-y-1 ${ev.voided ? 'opacity-50' : ''}`}
 style={{ borderColor: ev.voided ? 'rgba(244,63,94,0.4)' : 'rgba(255,255,255,0.1)' }}
 >
 <div className="flex items-baseline gap-1.5 text-xs">
 {ev.actorRole?.icon && <span className="text-sm leading-none">{ev.actorRole.icon}</span>}
 <span className={`font-medium text-[#F5F5F5] ${ev.voided ? 'line-through' : ''}`}>
 {ev.actorRole?.name ? tRole(ev.actorRole.name) : ev.action}
 </span>
 {ev.actor && (
 <span className="text-[10px] text-[#A3A3A3]">
 {ev.actor.name && ev.actor.name !== 'Игрок' ? ev.actor.name : ''}
 </span>
 )}
 <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-300/80">
 {ev.verb}
 </span>
 </div>
 {ev.targets.length === 0 ? (
 <p className="text-[11px] text-[#A3A3A3] italic pl-1">—</p>
 ) : (
 <ul className="pl-1 space-y-0.5">
 {ev.targets.map((tg, i) => (
 <li key={`${ev.id}-${tg.seat}-${i}`} className="flex items-center gap-1.5 text-xs">
 <span className="text-[#A3A3A3]">→</span>
 <span className="font-medium text-[#F5F5F5] truncate max-w-[8rem]">{tg.name}</span>
 {tg.role && showRoles && (
 <span className="text-[10px] text-[#A3A3A3]">{tg.role.icon} {tRole(tg.role.name)}</span>
 )}
 {tg.outcome && (
 <span
 className="ml-auto text-[10px] px-1.5 py-0.5 rounded border"
 style={{
 borderColor: tg.willDie ? 'rgba(244,63,94,0.5)' : 'rgba(16,185,129,0.4)',
 background: tg.willDie ? 'rgba(244,63,94,0.1)' : 'rgba(16,185,129,0.08)',
 color: tg.willDie ? '#FCA5A5' : '#6EE7B7',
 }}
 >
 {tg.outcome}
 </span>
 )}
 </li>
 ))}
 </ul>
 )}
 {ev.hostComment && (
 <p className="text-[11px] text-amber-300/90 italic pl-1">💬 {ev.hostComment}</p>
 )}
 <div className="flex items-center gap-1 pt-1 border-t border-white/5 mt-1">
 <button
 onClick={() => patchEventLocal(ev.id, { voided: !ev.voided })}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-rose-500/50 hover:text-rose-300 text-[#A3A3A3] transition-colors"
 title={ev.voided ? 'Восстановить событие' : 'Отменить событие'}
 >
 {ev.voided ? '↺ Восст.' : '× Отменить'}
 </button>
 <button
 onClick={() => {
 const v = window.prompt('Изменить результат (оставьте пустым, чтобы сбросить)', ev.hostOverride || '');
 if (v !== null) patchEventLocal(ev.id, { host_override: v });
 }}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-amber-500/50 hover:text-amber-300 text-[#A3A3A3] transition-colors"
 title="Изменить результат"
 >
 ✎ Результат
 </button>
 <button
 onClick={() => {
 const v = window.prompt('Комментарий хоста', ev.hostComment || '');
 if (v !== null) patchEventLocal(ev.id, { host_comment: v });
 }}
 className="text-[10px] px-1.5 py-0.5 border border-white/10 hover:border-blue-500/50 hover:text-blue-300 text-[#A3A3A3] transition-colors"
 title="Добавить комментарий"
 >
 💬 Комм.
 </button>
 </div>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* Round-level host comment */}
 {(() => {
 const key = `${reportModal.round}:${reportModal.phase === 'first_night' ? 'first_night' : reportModal.phase}`;
 const existing = (game.round_comments || {})[key]?.comment || '';
 return (
 <div className="pt-3 border-t border-white/10 space-y-2">
 <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
 💬 Комментарий хоста к этому раунду
 </p>
 <textarea
 defaultValue={existing}
 placeholder="Свободные заметки хоста, изменения алгоритма, спорные моменты…"
 rows={3}
 data-testid="round-comment-textarea"
 className="w-full bg-black/50 border border-white/15 px-2 py-1.5 text-sm text-[#F5F5F5] outline-none focus:border-amber-400/60"
 onBlur={(e) => {
 const v = e.target.value;
 if (v !== existing) {
 saveRoundComment(reportModal.round, reportModal.phase === 'first_night' ? 'first_night' : reportModal.phase, v);
 }
 }}
 />
 </div>
 );
 })()}
 </motion.div>
 </div>
 );
 })()}
 </div>
 );
}
